import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ArrowLeft,
  Download,
  Loader2,
  FileText,
  MoreVertical,
  Edit,
  Tag,
  Sparkles,
  Share2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import type { Tables } from "@/integrations/supabase/types";
import { getCategoryConfig, getTypeConfig, getCategoryLabel } from "@/constants/ui-mapping";
import { SmartDocumentViewer } from "@/components/viewer/SmartDocumentViewer";

type DocumentType = Tables<"documents">;

interface MobileDocumentViewerProps {
  document: DocumentType | null;
  open: boolean;
  onClose: () => void;
}

type TabType = "document" | "infos";

export function MobileDocumentViewer({ document, open, onClose }: MobileDocumentViewerProps) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>("document");
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState(false);

  // Parse key_fields safely
  const keyFields = document?.key_fields as Record<string, any> | null;

  // Fetch file URL and update last_viewed_at when document changes
  useEffect(() => {
    if (!document || !open) {
      if (!open) {
        setFileUrl(null);
        setActiveTab("document");
      }
      return;
    }

    const fetchFile = async () => {
      setIsFetching(true);

      const { data, error } = await supabase.storage
        .from("documents")
        .createSignedUrl(document.file_path, 300);

      if (error) {
        console.error("Error fetching file:", error);
        setFileUrl(null);
      } else {
        setFileUrl(data.signedUrl);
      }
      setIsFetching(false);
    };

    // Update last_viewed_at when opening preview
    const updateLastViewed = async () => {
      await supabase
        .from("documents")
        .update({ last_viewed_at: new Date().toISOString() })
        .eq("id", document.id);
    };

    fetchFile();
    updateLastViewed();
  }, [document?.id, open]);

  const handleDownload = async () => {
    if (!document || !fileUrl) return;

    const link = window.document.createElement("a");
    link.href = fileUrl;
    link.download = document.file_name;
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const formatAmount = (amount: number | string | null, currency?: string) => {
    if (!amount) return null;
    const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: currency || "EUR",
    }).format(numAmount);
  };

  if (!document) return null;

  const typeConfig = getTypeConfig(document.doc_type);
  const categoryConfig = document.category ? getCategoryConfig(document.category) : null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 bg-background flex flex-col"
        >
          {/* Header */}
          <header className="shrink-0 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="flex items-center justify-between px-2 py-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="shrink-0"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              
              <h1 className="flex-1 text-center font-semibold text-sm truncate px-2">
                {typeConfig.label}
              </h1>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="shrink-0">
                    <MoreVertical className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate(`/documents/${document.id}`)}>
                    <Edit className="w-4 h-4 mr-2" />
                    Modifier
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleDownload} disabled={!fileUrl}>
                    <Download className="w-4 h-4 mr-2" />
                    Télécharger
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <Share2 className="w-4 h-4 mr-2" />
                    Partager
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Tabs */}
            <div className="flex border-t">
              <button
                onClick={() => setActiveTab("document")}
                className={cn(
                  "flex-1 py-3 text-sm font-medium transition-colors relative",
                  activeTab === "document"
                    ? "text-primary"
                    : "text-muted-foreground"
                )}
              >
                Document
                {activeTab === "document" && (
                  <motion.div
                    layoutId="tab-indicator"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                  />
                )}
              </button>
              <button
                onClick={() => setActiveTab("infos")}
                className={cn(
                  "flex-1 py-3 text-sm font-medium transition-colors relative",
                  activeTab === "infos"
                    ? "text-primary"
                    : "text-muted-foreground"
                )}
              >
                Infos
                {activeTab === "infos" && (
                  <motion.div
                    layoutId="tab-indicator"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                  />
                )}
              </button>
            </div>
          </header>

          {/* Tab Content */}
          <div className="flex-1 overflow-hidden">
            <AnimatePresence mode="wait">
              {activeTab === "document" ? (
                <motion.div
                  key="document"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.15 }}
                  className="h-full"
                >
                  {/* Document Preview - Full Screen using unified viewer */}
                  <div className="h-full bg-muted/30">
                    {isFetching ? (
                      <div className="flex items-center justify-center h-full">
                        <Loader2 className="w-8 h-8 animate-spin text-primary" />
                      </div>
                    ) : !fileUrl ? (
                      <div className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground">
                        <FileText className="w-12 h-12" />
                        <p className="text-sm">Aperçu non disponible</p>
                        <Button variant="outline" size="sm" onClick={handleDownload}>
                          <Download className="w-4 h-4 mr-2" />
                          Télécharger
                        </Button>
                      </div>
                    ) : (
                      <SmartDocumentViewer
                        url={fileUrl}
                        title={document.title}
                        mimeType={document.mime_type || 'application/octet-stream'}
                        className="h-full"
                        showControls
                      />
                    )}
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="infos"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.15 }}
                  className="h-full"
                >
                  <ScrollArea className="h-full">
                    <div className="p-4 space-y-4 pb-24">
                      {/* Main Info Card */}
                      <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-4 border border-primary/10">
                        <div className="flex items-start justify-between mb-3">
                          <Badge variant="secondary" className="text-xs">
                            {typeConfig.emoji} {typeConfig.label}
                          </Badge>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <Edit className="w-4 h-4" />
                          </Button>
                        </div>

                        {/* Amount */}
                        {(keyFields?.amount || document.total_amount) && (
                          <div className="mb-4">
                            <p className="text-3xl font-bold">
                              {formatAmount(keyFields?.amount || document.total_amount, keyFields?.currency)}
                            </p>
                            {keyFields?.vat && (
                              <p className="text-sm text-muted-foreground mt-1">
                                dont {formatAmount(keyFields.vat)} de TVA
                              </p>
                            )}
                          </div>
                        )}

                        {/* Issuer */}
                        {document.issuer_name && (
                          <div className="mb-4">
                            <p className="text-xs text-muted-foreground uppercase tracking-wide">
                              Émetteur
                            </p>
                            <p className="font-semibold text-lg">{document.issuer_name}</p>
                          </div>
                        )}

                        {/* Dates */}
                        <div className="grid grid-cols-2 gap-4">
                          {document.doc_date && (
                            <div>
                              <p className="text-xs text-muted-foreground uppercase tracking-wide">
                                Date
                              </p>
                              <p className="font-medium">{formatDate(document.doc_date)}</p>
                            </div>
                          )}
                          {document.due_date && (
                            <div>
                              <p className="text-xs text-muted-foreground uppercase tracking-wide">
                                Échéance
                              </p>
                              <p className="font-medium">{formatDate(document.due_date)}</p>
                            </div>
                          )}
                        </div>

                        {/* Reference */}
                        {document.reference_number && (
                          <div className="mt-4 pt-4 border-t border-primary/10">
                            <p className="text-xs text-muted-foreground uppercase tracking-wide">
                              Référence
                            </p>
                            <p className="font-mono text-sm">{document.reference_number}</p>
                          </div>
                        )}
                      </div>

                      {/* AI Summary */}
                      {document.ai_summary && (
                        <div className="bg-card rounded-xl p-4 border">
                          <div className="flex items-center gap-2 mb-2">
                            <Sparkles className="w-4 h-4 text-primary" />
                            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                              Résumé IA
                            </p>
                          </div>
                          <p className="text-sm leading-relaxed">{document.ai_summary}</p>
                        </div>
                      )}

                      {/* Category & Tags */}
                      <div className="bg-card rounded-xl p-4 border">
                        <div className="flex items-center gap-2 mb-3">
                          <Tag className="w-4 h-4 text-muted-foreground" />
                          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                            Catégorie
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {document.category && categoryConfig && (
                            <span className={cn("text-sm px-3 py-1.5 rounded-full font-medium", categoryConfig.color)}>
                              {categoryConfig.emoji} {getCategoryLabel(document.category)}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Document Details */}
                      <div className="bg-card rounded-xl p-4 border">
                        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-3">
                          Détails du fichier
                        </p>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Nom du fichier</span>
                            <span className="font-medium truncate ml-4 max-w-[200px]">
                              {document.file_name}
                            </span>
                          </div>
                          {document.file_size && (
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Taille</span>
                              <span className="font-medium">
                                {(document.file_size / 1024 / 1024).toFixed(2)} Mo
                              </span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Ajouté le</span>
                            <span className="font-medium">{formatDate(document.created_at)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </ScrollArea>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Floating Bottom Bar */}
          <div className="shrink-0 px-4 py-3 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={handleDownload}
                disabled={!fileUrl}
              >
                <Download className="w-4 h-4 mr-2" />
                Télécharger
              </Button>
              <Button
                className="flex-1"
                onClick={() => {
                  onClose();
                  navigate(`/documents/${document.id}`);
                }}
              >
                Voir détails
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
